import React, { useState, useEffect, useCallback } from 'react';
import useAuthStore from '../lib/auth-store';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
  PenLine, FileCheck, Send, RefreshCcw, Download, Eye, Clock,
  CheckCircle2, XCircle, AlertTriangle, FileText, Ban, Loader2,
  Shield, ExternalLink, Inbox
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_STYLES = {
  created:     { color: 'bg-blue-100 text-blue-800 border-blue-200', icon: Clock, label: 'Created' },
  sent:        { color: 'bg-violet-100 text-violet-800 border-violet-200', icon: Send, label: 'Sent' },
  inprogress:  { color: 'bg-indigo-100 text-indigo-800 border-indigo-200', icon: Send, label: 'In Progress' },
  viewed:      { color: 'bg-amber-100 text-amber-800 border-amber-200', icon: Eye, label: 'Viewed' },
  signed:      { color: 'bg-emerald-100 text-emerald-800 border-emerald-200', icon: CheckCircle2, label: 'Signed' },
  completed:   { color: 'bg-green-100 text-green-800 border-green-200', icon: FileCheck, label: 'Completed' },
  declined:    { color: 'bg-red-100 text-red-800 border-red-200', icon: XCircle, label: 'Declined' },
  revoked:     { color: 'bg-gray-100 text-gray-600 border-gray-200', icon: Ban, label: 'Revoked' },
  expired:     { color: 'bg-orange-100 text-orange-700 border-orange-200', icon: AlertTriangle, label: 'Expired' },
  send_failed: { color: 'bg-red-100 text-red-700 border-red-200', icon: AlertTriangle, label: 'Send Failed' },
};

function SigStatusBadge({ status }) {
  const cfg = STATUS_STYLES[status] || STATUS_STYLES.created;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${cfg.color}`}
      data-testid={`sig-status-${status}`}>
      <Icon className="w-3 h-3" />
      {cfg.label}
    </span>
  );
}

export default function ESignaturePage() {
  const { token, user } = useAuthStore();
  const [config, setConfig] = useState(null);
  const [signatures, setSignatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sendOpen, setSendOpen] = useState(false);
  const [signOpen, setSignOpen] = useState(false);
  const [signingUrl, setSigningUrl] = useState(null);
  const [signingRecord, setSigningRecord] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [refreshingId, setRefreshingId] = useState(null);

  const isAdmin = user?.role === 'super_admin' || user?.role === 'iso';
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const checkConfig = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/esign/config`, { headers });
      const data = await res.json();
      setConfig(data);
    } catch { setConfig({ configured: false }); }
  }, [token]);

  const loadSignatures = useCallback(async () => {
    try {
      const params = statusFilter !== 'all' ? `?status=${statusFilter}` : '';
      const res = await fetch(`${API}/api/esign/tenant/signatures${params}`, { headers });
      const data = await res.json();
      setSignatures(data.items || []);
    } catch { setSignatures([]); }
    setLoading(false);
  }, [token, statusFilter]);

  useEffect(() => {
    checkConfig();
    loadSignatures();
  }, [checkConfig, loadSignatures]);

  const refreshStatus = async (trackingId) => {
    setRefreshingId(trackingId);
    try {
      const res = await fetch(`${API}/api/esign/refresh/${trackingId}`, {
        method: 'POST', headers
      });
      if (!res.ok) throw new Error('Refresh failed');
      const data = await res.json();
      toast.success(`Status updated: ${data.status}`);
      loadSignatures();
    } catch (err) { toast.error(err.message); }
    setRefreshingId(null);
  };

  const getSigningLink = async (trackingId, record) => {
    try {
      const res = await fetch(`${API}/api/esign/signing-link/${trackingId}`, { headers });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to get signing link');
      }
      const data = await res.json();
      setSigningUrl(data.url);
      setSigningRecord(record);
      setSignOpen(true);
    } catch (err) { toast.error(err.message); }
  };

  const downloadSigned = async (trackingId, title) => {
    try {
      const res = await fetch(`${API}/api/esign/download/${trackingId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `signed_${(title || 'document').replace(/\s+/g, '_')}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Download started');
    } catch (err) { toast.error(err.message); }
  };

  const revokeSignature = async (trackingId) => {
    if (!window.confirm('Are you sure you want to revoke this signature request?')) return;
    try {
      const res = await fetch(`${API}/api/esign/revoke/${trackingId}`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ reason: 'Revoked by admin' }),
      });
      if (!res.ok) throw new Error('Revoke failed');
      toast.success('Signature request revoked');
      loadSignatures();
    } catch (err) { toast.error(err.message); }
  };

  return (
    <div className="space-y-6" data-testid="esignature-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold font-[var(--font-heading)]">E-Signatures</h1>
          <p className="text-sm text-muted-foreground mt-1">BoldSign — Send, track, and manage document signatures</p>
        </div>
        <div className="flex items-center gap-3">
          {config && (
            <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border ${
              config.configured
                ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                : 'bg-red-50 text-red-700 border-red-200'
            }`} data-testid="esign-connection-status">
              <Shield className="w-3.5 h-3.5" />
              {config.configured ? 'BoldSign Connected' : 'Not Configured'}
            </div>
          )}
          {isAdmin && config?.configured && (
            <Button onClick={() => setSendOpen(true)} data-testid="send-for-signature-btn">
              <PenLine className="h-4 w-4 mr-2" /> Send for Signature
            </Button>
          )}
        </div>
      </div>

      {/* Not configured banner */}
      {config && !config.configured && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 flex items-start gap-3" data-testid="esign-not-configured">
          <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-amber-800">E-Signature Not Configured</p>
            <p className="text-xs text-amber-700 mt-1">
              Add your <code className="bg-amber-100 px-1 rounded">BOLDSIGN_API_KEY</code> to the backend environment to enable document signing.
            </p>
          </div>
        </div>
      )}

      {/* Filter */}
      {config?.configured && (
        <div className="flex items-center gap-3">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[180px]" data-testid="sig-status-filter">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="created">Created</SelectItem>
              <SelectItem value="sent">Sent</SelectItem>
              <SelectItem value="viewed">Viewed</SelectItem>
              <SelectItem value="signed">Signed</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="declined">Declined</SelectItem>
              <SelectItem value="revoked">Revoked</SelectItem>
              <SelectItem value="expired">Expired</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={loadSignatures} data-testid="refresh-signatures-btn">
            <RefreshCcw className="w-3.5 h-3.5 mr-1.5" /> Refresh
          </Button>
        </div>
      )}

      {/* Signatures List */}
      {loading ? (
        <div className="flex items-center justify-center h-40">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      ) : !config?.configured ? null : signatures.length === 0 ? (
        <div className="text-center py-16 border border-dashed rounded-xl" data-testid="no-signatures">
          <Inbox className="h-12 w-12 mx-auto text-muted-foreground/20 mb-3" />
          <p className="text-sm text-muted-foreground">No signature requests yet</p>
          <p className="text-xs text-muted-foreground mt-1">Send a document for signature to get started.</p>
        </div>
      ) : (
        <div className="rounded-xl border border-border bg-card overflow-hidden" data-testid="signatures-table">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/40">
                  <th className="text-left px-4 py-3 font-medium">Document</th>
                  <th className="text-left px-4 py-3 font-medium">Signer</th>
                  <th className="text-left px-4 py-3 font-medium">Status</th>
                  <th className="text-left px-4 py-3 font-medium">Sent</th>
                  <th className="text-left px-4 py-3 font-medium">Signed</th>
                  <th className="text-center px-4 py-3 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {signatures.map(sig => (
                  <tr key={sig.id} className="border-b last:border-0 hover:bg-muted/30 transition-colors"
                    data-testid={`sig-row-${sig.id}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                        <div>
                          <p className="font-medium text-sm truncate max-w-[200px]">{sig.title || 'Untitled'}</p>
                          {sig.document_type && (
                            <Badge variant="outline" className="text-[10px] mt-0.5">{sig.document_type}</Badge>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-sm">{sig.signer_name}</p>
                      <p className="text-xs text-muted-foreground">{sig.signer_email}</p>
                    </td>
                    <td className="px-4 py-3">
                      <SigStatusBadge status={sig.status} />
                    </td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">
                      {sig.sent_at ? new Date(sig.sent_at).toLocaleDateString() : '—'}
                    </td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">
                      {sig.signed_at ? new Date(sig.signed_at).toLocaleDateString() : '—'}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-1">
                        {/* Refresh status */}
                        <Button variant="ghost" size="sm"
                          onClick={() => refreshStatus(sig.id)}
                          disabled={refreshingId === sig.id}
                          data-testid={`refresh-sig-${sig.id}`}
                          title="Refresh status from BoldSign">
                          {refreshingId === sig.id
                            ? <Loader2 className="w-4 h-4 animate-spin" />
                            : <RefreshCcw className="w-4 h-4" />}
                        </Button>

                        {/* Get signing link (for pending/sent/created) */}
                        {sig.enable_embedded && ['created', 'sent', 'viewed', 'inprogress'].includes(sig.status) && (
                          <Button variant="ghost" size="sm"
                            onClick={() => getSigningLink(sig.id, sig)}
                            data-testid={`sign-btn-${sig.id}`}
                            title="Open signing view">
                            <PenLine className="w-4 h-4" />
                          </Button>
                        )}

                        {/* Download signed doc */}
                        {['signed', 'completed'].includes(sig.status) && (
                          <Button variant="ghost" size="sm"
                            onClick={() => downloadSigned(sig.id, sig.title)}
                            data-testid={`download-sig-${sig.id}`}
                            title="Download signed document">
                            <Download className="w-4 h-4" />
                          </Button>
                        )}

                        {/* Revoke (admin only, pending status) */}
                        {isAdmin && ['created', 'sent', 'viewed', 'inprogress'].includes(sig.status) && (
                          <Button variant="ghost" size="sm"
                            onClick={() => revokeSignature(sig.id)}
                            data-testid={`revoke-sig-${sig.id}`}
                            title="Revoke signature request"
                            className="text-red-500 hover:text-red-700">
                            <Ban className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-3 border-t bg-muted/30 text-xs text-muted-foreground">
            {signatures.length} signature request{signatures.length !== 1 ? 's' : ''}
          </div>
        </div>
      )}

      {/* Send for Signature Dialog */}
      <SendForSignatureDialog
        open={sendOpen}
        onClose={() => setSendOpen(false)}
        token={token}
        onSent={() => { loadSignatures(); setSendOpen(false); }}
      />

      {/* Embedded Signing Dialog */}
      <Dialog open={signOpen} onOpenChange={v => { if (!v) { setSignOpen(false); setSigningUrl(null); } }}>
        <DialogContent className="max-w-4xl max-h-[90vh]" data-testid="signing-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PenLine className="w-5 h-5" />
              Sign Document: {signingRecord?.title || 'Document'}
            </DialogTitle>
          </DialogHeader>
          {signingUrl ? (
            <iframe
              title="BoldSign Signing"
              src={signingUrl}
              className="w-full h-[70vh] rounded-lg border"
              allow="camera; microphone"
              data-testid="signing-iframe"
            />
          ) : (
            <div className="flex items-center justify-center h-40">
              <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
            </div>
          )}
          <DialogFooter className="flex items-center justify-between">
            <Button variant="outline" onClick={() => {
              if (signingRecord) refreshStatus(signingRecord.id);
            }} data-testid="signing-refresh-btn">
              <RefreshCcw className="w-4 h-4 mr-2" /> Refresh Status
            </Button>
            <Button variant="outline" onClick={() => { setSignOpen(false); setSigningUrl(null); loadSignatures(); }}
              data-testid="signing-close-btn">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function SendForSignatureDialog({ open, onClose, token, onSent }) {
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState('');
  const [signerName, setSignerName] = useState('');
  const [signerEmail, setSignerEmail] = useState('');
  const [docType, setDocType] = useState('merchant_boarding');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  const handleSend = async () => {
    if (!file || !title || !signerName || !signerEmail) {
      toast.error('Please fill in all required fields');
      return;
    }
    setSending(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('title', title);
      formData.append('signer_name', signerName);
      formData.append('signer_email', signerEmail);
      formData.append('document_type', docType);
      formData.append('enable_embedded', 'true');
      if (message) formData.append('message', message);

      const res = await fetch(`${API}/api/esign/upload-and-send`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to send');
      }

      const data = await res.json();
      toast.success(`Document sent to ${signerEmail} for signature`);
      setFile(null); setTitle(''); setSignerName(''); setSignerEmail(''); setMessage('');
      onSent();
    } catch (err) { toast.error(err.message); }
    setSending(false);
  };

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="send-signature-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PenLine className="w-5 h-5" />
            Send Document for Signature
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="text-xs font-medium text-muted-foreground mb-1 block">PDF Document *</label>
            <Input
              type="file"
              accept=".pdf,application/pdf"
              onChange={e => setFile(e.target.files?.[0] || null)}
              data-testid="sig-file-input"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground mb-1 block">Document Title *</label>
            <Input value={title} onChange={e => setTitle(e.target.value)}
              placeholder="e.g. Merchant Boarding Agreement"
              data-testid="sig-title-input" />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground mb-1 block">Document Type</label>
            <Select value={docType} onValueChange={setDocType}>
              <SelectTrigger data-testid="sig-doctype-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="merchant_boarding">Merchant Boarding Agreement</SelectItem>
                <SelectItem value="equipment_financing">Equipment Financing</SelectItem>
                <SelectItem value="processing_agreement">Processing Agreement</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1 block">Signer Name *</label>
              <Input value={signerName} onChange={e => setSignerName(e.target.value)}
                placeholder="John Doe" data-testid="sig-name-input" />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1 block">Signer Email *</label>
              <Input value={signerEmail} onChange={e => setSignerEmail(e.target.value)}
                placeholder="john@example.com" type="email" data-testid="sig-email-input" />
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground mb-1 block">Message (optional)</label>
            <Input value={message} onChange={e => setMessage(e.target.value)}
              placeholder="Please review and sign this document"
              data-testid="sig-message-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSend} disabled={sending} data-testid="send-signature-submit">
            {sending ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending...</> : <><Send className="w-4 h-4 mr-2" /> Send for Signature</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
